import { Box, Typography } from "@mui/material";
import { useTranslation } from "common/locales";
import { convertToFeedStockAmountString } from "features/feedPurchaseDetail/generalMethods";
import {
  convertToFertilizerAmountString,
  getFertilizerTypeString,
} from "features/fertilizerInput/FertilizerEventListItem";
import { convertToYieldAmountString } from "features/yieldInput/generalMethods";
import {
  AnimalGroup,
  ArableField,
  CropYieldAmount,
  FertilizerEvent,
  Field,
  FieldPreparationEvent,
  GeneralFeedEvent,
  GeneralFeedPurchase,
  GeneralFeedPurchaseEvent,
  GrassYieldAmount,
  MeatYieldAmount,
  MilkYieldAmount,
  ReseedEvent,
  selectAllAnimalGroups,
  selectAllArableFields,
  selectAllFields,
  selectFieldsById,
  SOLD_OR_KEPT,
  useAppSelector,
  YieldEvent,
  YIELD_TYPES,
} from "model";

export function ReseedEventTableCell({
  reseedEvent,
}: {
  reseedEvent: ReseedEvent;
}) {
  const { t } = useTranslation();

  const title = `reseedEvents.show.titleName.${reseedEvent.reseedType}`;
  const { newGrassType } = reseedEvent;
  const fields: Field[] = useAppSelector(
    selectFieldsById(reseedEvent.fieldGuids)
  );

  const grassType = t(`fieldinput.grassType.option.${newGrassType}`);

  if (!fields || fields.length === 0) {
    return null;
  }

  const fieldLabel = fields.length === 1 ? "Field:" : "Fields:";
  const fieldNames = fields.map((field) => field.name).join(", ");

  return (
    <Box sx={{ py: { xs: 0, sm: 2 }, px: { xs: 0, sm: 2 } }}>
      <Typography component="div" variant="body2" sx={{ mb: 1 }}>
        <strong>{fieldLabel}</strong> {fieldNames}
      </Typography>
      <Typography component="div" variant="body2" sx={{ mb: 1 }}>
        <strong>New grass type:</strong> {grassType}
      </Typography>
      {reseedEvent.seedingMethod && (
        <Typography component="div" variant="body2" sx={{ mb: 1 }}>
          <strong>Seeding method:</strong>{" "}
          {t(`reseedEvent.seedingMethod.option.${reseedEvent.seedingMethod}`)}
        </Typography>
      )}
      {reseedEvent.note && (
        <Typography component="div" variant="body2" sx={{ mb: 1 }}>
          <strong>Note:</strong> {reseedEvent.note}
        </Typography>
      )}
    </Box>
  );
}

export function FeedEventTableCell({
  feedEvent,
}: {
  feedEvent: GeneralFeedEvent;
}) {
  const { t } = useTranslation();
  const locale = useAppSelector((state) => state.app.locale);

  const { animalGroupGuids, feed } = feedEvent;
  const allAnimalGroups: AnimalGroup[] = useAppSelector(selectAllAnimalGroups);
  const animalGroups = allAnimalGroups.filter((animalGroup) =>
    animalGroupGuids.includes(animalGroup.guid)
  );

  const animalGroupLabel =
    animalGroups.length === 1 ? "Animal group:" : "Animal groups:";
  const animalGroupNames = animalGroups
    .map((animalGroup) => animalGroup.name)
    .join(", ");

  const feedItems = feed.map((singleFeed, index) => {
    const feedType = t(`feedPurchase.feedType.option.${singleFeed.feedType}`);
    const amount = convertToFeedStockAmountString(
      singleFeed.amount,
      locale,
      singleFeed.unitType,
      t
    );
    return `${feedType}: ${amount}`;
  });

  return (
    <Box sx={{ py: { xs: 0, sm: 2 }, px: { xs: 0, sm: 2 } }}>
      <Typography component="div" variant="body2" sx={{ mb: 1 }}>
        <strong>{animalGroupLabel}</strong> {animalGroupNames}
      </Typography>
      <Typography component="div" variant="body2">
        <strong>Feed:</strong> {feedItems.join(", ")}
      </Typography>
      {feedEvent.note && (
        <Typography component="div" variant="body2" sx={{ mt: 1 }}>
          <strong>Note:</strong> {feedEvent.note}
        </Typography>
      )}
    </Box>
  );
}

export function FeedPurchaseEventTableCell({
  feedPurchaseEvent,
}: {
  feedPurchaseEvent: GeneralFeedPurchaseEvent;
}) {
  const { t } = useTranslation();
  const locale = useAppSelector((state) => state.app.locale);

  const { feedPurchases } = feedPurchaseEvent;

  const purchaseItems = feedPurchases.map(
    (feedPurchase: GeneralFeedPurchase) => {
      const feedType = t(
        `feedPurchase.feedType.option.${feedPurchase.feedType}`
      );
      const amount = convertToFeedStockAmountString(
        feedPurchase.amount,
        locale,
        feedPurchase.unitType,
        t
      );
      const cost = feedPurchase.cost
        ? feedPurchase.cost.toLocaleString(locale, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
            useGrouping: true,
          }) + " £"
        : "price unknown";
      return `${feedType} (${amount}, ${cost})`;
    }
  );

  return (
    <Box sx={{ py: { xs: 0, sm: 2 }, px: { xs: 0, sm: 2 } }}>
      <Typography component="div" variant="body2">
        <strong>Purchases:</strong> {purchaseItems.join("; ")}
      </Typography>
    </Box>
  );
}

export function YieldEventTableCell({
  yieldEvent,
}: {
  yieldEvent: YieldEvent;
}) {
  const { t } = useTranslation();
  const locale = useAppSelector((state) => state.app.locale);

  let animalGroups: string[] = [];
  if (
    yieldEvent.yieldDetails.yieldType === YIELD_TYPES.MEAT ||
    yieldEvent.yieldDetails.yieldType === YIELD_TYPES.MILK
  ) {
    animalGroups = yieldEvent.yieldDetails.animalGroupGuids;
  }
  const allAnimalGroups: AnimalGroup[] = useAppSelector(selectAllAnimalGroups);
  const selectedAnimalGroups = allAnimalGroups.filter((animalGroup) =>
    animalGroups.includes(animalGroup.guid)
  );

  let grassFields: string[] = [];
  let arableFields: string[] = [];
  if (YIELD_TYPES.GRASS === yieldEvent.yieldDetails.yieldType) {
    grassFields = yieldEvent.yieldDetails.fieldGuids;
  }
  if (YIELD_TYPES.CROP === yieldEvent.yieldDetails.yieldType) {
    arableFields = yieldEvent.yieldDetails.fieldGuids;
  }
  const allGrassFields: Field[] = useAppSelector(selectAllFields);
  const allArableFields: ArableField[] = useAppSelector(selectAllArableFields);

  const selectedGrassFields = allGrassFields.filter((grassField) =>
    grassFields.includes(grassField.guid)
  );

  const selectedArableFields = allArableFields.filter((arableField) =>
    arableFields.includes(arableField.guid)
  );

  const yieldTypeLabel = t(
    `yieldEvent.yieldType.option.${yieldEvent.yieldDetails.yieldType}`
  );

  const animalGroupLabel =
    selectedAnimalGroups.length === 1 ? "Animal group:" : "Animal groups:";
  const fieldLabel =
    selectedGrassFields.length + selectedArableFields.length === 1
      ? "Field:"
      : "Fields:";
  const allFieldNames = [
    ...selectedGrassFields.map((f) => f.name),
    ...selectedArableFields.map((f) => f.name),
  ].join(", ");

  const yieldAmounts = yieldEvent.yieldDetails.yieldAmounts.map(
    (
      singleYield:
        | MilkYieldAmount
        | GrassYieldAmount
        | MeatYieldAmount
        | CropYieldAmount,
      index: number
    ) => {
      const yieldString = convertToYieldAmountString(
        singleYield.amount,
        locale,
        singleYield.unitType,
        t
      );

      let yieldTypeDetail = "";
      if ("grassYieldType" in singleYield) {
        yieldTypeDetail = t(
          `yieldEvent.yieldType.option.${singleYield.grassYieldType}`
        );
      } else if ("cropYieldType" in singleYield) {
        yieldTypeDetail = t(
          `yieldEvent.yieldType.option.${singleYield.cropYieldType}`
        );
      }

      if (singleYield.soldOrKept === SOLD_OR_KEPT.KEPT) {
        const keptText = t("yieldEvent.amount.kept", {
          amount: yieldString,
        });
        return yieldTypeDetail ? `${yieldTypeDetail} - ${keptText}` : keptText;
      } else {
        const priceString = singleYield.revenue
          ? singleYield.revenue.toLocaleString(locale, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
              useGrouping: true,
            }) + " £"
          : "unknown revenue";
        const soldText = t("yieldEvent.amount.sold", {
          amount: yieldString,
          soldPrice: priceString,
        });
        return yieldTypeDetail ? `${yieldTypeDetail} - ${soldText}` : soldText;
      }
    }
  );

  return (
    <Box sx={{ py: { xs: 0, sm: 2 }, px: { xs: 0, sm: 2 } }}>
      <Typography component="div" variant="body2" sx={{ mb: 1 }}>
        <strong>Type:</strong> {yieldTypeLabel} yield
      </Typography>
      {selectedAnimalGroups.length > 0 && (
        <Typography component="div" variant="body2" sx={{ mb: 1 }}>
          <strong>{animalGroupLabel}</strong>{" "}
          {selectedAnimalGroups.map((ag) => ag.name).join(", ")}
        </Typography>
      )}
      {(selectedGrassFields.length > 0 || selectedArableFields.length > 0) && (
        <Typography component="div" variant="body2" sx={{ mb: 1 }}>
          <strong>{fieldLabel}</strong> {allFieldNames}
        </Typography>
      )}
      {yieldAmounts.length > 0 && (
        <Typography component="div" variant="body2" sx={{ mb: 1 }}>
          <strong>Yield amounts:</strong> {yieldAmounts.join("; ")}
        </Typography>
      )}
      {yieldEvent.note && (
        <Typography component="div" variant="body2" sx={{ mt: 1 }}>
          <strong>Note:</strong> {yieldEvent.note}
        </Typography>
      )}
    </Box>
  );
}

export function FertilizerEventTableCell({
  fertilizerEvent,
}: {
  fertilizerEvent: FertilizerEvent;
}) {
  const { t } = useTranslation();
  const locale = useAppSelector((state) => state.app.locale);

  const allGrassFields: Field[] = useAppSelector(selectAllFields);
  const allArableFields: ArableField[] = useAppSelector(selectAllArableFields);

  const selectedGrassField = allGrassFields.filter((grassField) =>
    fertilizerEvent.fieldGuids.includes(grassField.guid)
  );

  const selectedArableFields = allArableFields.filter((arableField) =>
    fertilizerEvent.fieldGuids.includes(arableField.guid)
  );

  const fieldNames = [
    selectedArableFields.map((field) => field.name),
    selectedGrassField.map((field) => field.name),
  ]
    .flat()
    .join(", ");

  const fertilizerAmountString = convertToFertilizerAmountString(
    fertilizerEvent.totalAmount,
    locale,
    fertilizerEvent.totalAmountUnit,
    t
  );

  const fertilizerType = getFertilizerTypeString(fertilizerEvent, t);
  const fieldLabel = fieldNames.split(", ").length === 1 ? "Field:" : "Fields:";

  return (
    <Box sx={{ py: { xs: 0, sm: 2 }, px: { xs: 0, sm: 2 } }}>
      <Typography component="div" variant="body2" sx={{ mb: 1 }}>
        <strong>Type:</strong> {fertilizerType}
      </Typography>
      <Typography component="div" variant="body2" sx={{ mb: 1 }}>
        <strong>{fieldLabel}</strong> {fieldNames}
      </Typography>
      <Typography component="div" variant="body2" sx={{ mb: 1 }}>
        <strong>Amount:</strong> {fertilizerAmountString}
      </Typography>
      {fertilizerEvent.note && (
        <Typography component="div" variant="body2">
          <strong>Note:</strong> {fertilizerEvent.note}
        </Typography>
      )}
    </Box>
  );
}

export function FieldPreparationEventTableCell({
  fieldPreparationEvent,
}: {
  fieldPreparationEvent: FieldPreparationEvent;
}) {
  const { t } = useTranslation();

  const allGrassFields: Field[] = useAppSelector(selectAllFields);
  const allArableFields: ArableField[] = useAppSelector(selectAllArableFields);

  const selectedGrassFields = allGrassFields.filter((grassField) =>
    fieldPreparationEvent.fieldGuids.includes(grassField.guid)
  );

  const selectedArableField = allArableFields.filter((arableField) =>
    fieldPreparationEvent.fieldGuids.includes(arableField.guid)
  );
  const fieldNames = selectedGrassFields
    .map((field: Field) => field.name)
    .concat(selectedArableField.map((field: ArableField) => field.name))
    .join(", ");

  const fieldLabel = fieldNames.split(", ").length === 1 ? "Field:" : "Fields:";
  const machineryNames = fieldPreparationEvent.machinery
    .map((machine) => t(`yieldEventInput.machinery.options.${machine}`))
    .join(", ");

  return (
    <Box sx={{ py: { xs: 0, sm: 2 }, px: { xs: 0, sm: 2 } }}>
      <Typography component="div" variant="body2" sx={{ mb: 1 }}>
        <strong>{fieldLabel}</strong> {fieldNames}
      </Typography>
      <Typography component="div" variant="body2" sx={{ mb: 1 }}>
        <strong>{t("fieldPreparationEvent.fuel.name")}:</strong>{" "}
        {fieldPreparationEvent.fuel} litre
      </Typography>
      <Typography component="div" variant="body2" sx={{ mb: 1 }}>
        <strong>{t("fieldPreparationEvent.machinery.name")}:</strong>{" "}
        {machineryNames}
      </Typography>
      {fieldPreparationEvent.note && (
        <Typography component="div" variant="body2">
          <strong>Note:</strong> {fieldPreparationEvent.note}
        </Typography>
      )}
    </Box>
  );
}
